import { useMutation, useQuery } from '@apollo/client/react';
import { type Editor } from '@tiptap/react';
import { type PropsWithChildren, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/Dialog';
import { EditButton } from '@/components/EditButton';
import FileInput from '@/components/FileInput';
import RichTextField from '@/components/RichTextField';
import RichTextFieldReadOnly from '@/components/RichTextFieldReadOnly';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Spinner } from '@/components/ui/spinner';
import { fileToBase64 } from '@/lib/utils';

import { GET_FINANCIAL_CASES, UPSERT_FINANCIAL_CASE } from './graphql';
import type { DealFinancialCase, GetFinancialCasesResponse } from './types';

const CASE_TYPES = [
  { label: 'Management Growth Case Projections', type: 'growth' },
  { label: 'SM Downside Case Projections', type: 'downside' },
  { label: 'SM Base Case Projections', type: 'base' }
] as const;

const SectionHeader = ({
  children,
  title
}: PropsWithChildren<{ title: string }>) => (
  <div className="mb-2 flex items-center justify-between gap-3">
    <h2 className="text-lg font-semibold">{title}</h2>
    <div className="flex items-center gap-2">{children}</div>
  </div>
);

const Card = ({ children }: PropsWithChildren) => (
  <div className="bg-card rounded-md border p-5">{children}</div>
);

const FinancialCaseSection = ({
  caseData,
  caseType,
  dealId,
  label
}: {
  caseData: DealFinancialCase | undefined;
  caseType: string;
  dealId: string;
  label: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const commentaryRef = useRef<Editor>(null);

  const [upsertFinancialCase, { loading: isUpdating }] = useMutation(
    UPSERT_FINANCIAL_CASE,
    { refetchQueries: [GET_FINANCIAL_CASES] }
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      const formData = new FormData(event.currentTarget);
      const imageFile = formData.get(`${caseType}-image-field`);
      let image: null | string = null;
      if (imageFile instanceof File && imageFile.size > 0) {
        image = await fileToBase64(imageFile);
      }

      const values: Record<string, unknown> = {
        dealOpportunityId: dealId,
        commentary: commentaryRef.current?.isEmpty
          ? null
          : commentaryRef.current?.getHTML()
      };
      if (image !== null) {
        values.image = image;
      }

      const { errors } = await upsertFinancialCase({
        variables: {
          dealOpportunityId: dealId,
          caseType,
          values
        }
      });

      if (errors?.length) {
        throw new Error(errors[0].message);
      }

      setIsOpen(false);
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  return (
    <div className="space-y-3">
      <SectionHeader title={label}>
        <Dialog onOpenChange={setIsOpen} open={isOpen}>
          <DialogTrigger asChild>
            <EditButton />
          </DialogTrigger>
          <DialogContent aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>
                {label}
                <Badge variant="yellow">Unsaved</Badge>
              </DialogTitle>
            </DialogHeader>
            <DialogBody>
              <form
                id={`financial-case-${caseType}-form`}
                noValidate
                onSubmit={event => void handleSubmit(event)}
              >
                <FieldGroup>
                  <Field>
                    <FieldLabel htmlFor={`${caseType}-commentary-field`}>
                      Commentary
                    </FieldLabel>
                    <RichTextField
                      defaultValue={caseData?.commentary ?? ''}
                      id={`${caseType}-commentary-field`}
                      ref={commentaryRef}
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor={`${caseType}-image-field`}>
                      Attachment
                    </FieldLabel>
                    <FileInput
                      accept="image/*"
                      data={caseData?.image ?? null}
                      id={`${caseType}-image-field`}
                      name={`${caseType}-image-field`}
                    />
                  </Field>
                </FieldGroup>
              </form>
            </DialogBody>
            <DialogFooter>
              <Button
                disabled={isUpdating}
                form={`financial-case-${caseType}-form`}
                type="submit"
              >
                {isUpdating && <Spinner />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SectionHeader>
      <Card>
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="text-sm font-medium">Commentary</div>
            <RichTextFieldReadOnly html={caseData?.commentary ?? ''} />
          </div>
          <div className="space-y-2">
            <div className="text-sm font-medium">Attachment</div>
            <FileInput
              accept="image/*"
              data={caseData?.image ?? null}
              readOnly
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

const FinancialCaseTab = ({ dealId }: { dealId: string }) => {
  const { data, loading } = useQuery<
    GetFinancialCasesResponse,
    { dealOpportunityId: string }
  >(GET_FINANCIAL_CASES, {
    variables: { dealOpportunityId: dealId }
  });

  if (loading) {
    return (
      <div className="grid h-64 place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  const cases = data?.financialCasesByDeal ?? [];

  return (
    <div className="space-y-7 pt-5">
      {CASE_TYPES.map(({ label, type }) => (
        <FinancialCaseSection
          caseData={cases.find(c => c.caseType === type)}
          caseType={type}
          dealId={dealId}
          key={type}
          label={label}
        />
      ))}
    </div>
  );
};

export default FinancialCaseTab;
